/**
* Radio buttons proxy
* Works with the symbol in RadioButtonGroup.fla
*
@author  RLB 10/7/2020
* Requires group of symbols containing up to 10 buttons, each with stage instance name rb0, rb1, ... rb9.
* Buttons must be placed at the desired positions and spacings. Labels will be automatically placed next to the buttons.
*	nButtons: number	Number of buttons to show. Max is 10.  DO THIS FIRST.
* 	selected : number. Sets or gets the index of the currently selected radio button. -1 = select none.
* 	labelSide: string.   "left", "right"
*	labelFont: string. e.g. Any valid value for the CSS font attribute is acceptable (ex. "bold 36px Arial").   https://createjs.com/docs/easeljs/classes/Text.html
*	labelColor: string		Whatever is accepted by UtilSP.c2rgba
*	labelWidth: string		Wrap width for text label. 0 = no wrap.
*	setLabelText(i, str)
@throws 'CLICKEDRADIOBUTTON'
*/
import AnimateProxyBase from "SATPROF_LIBRARY/DropinSupervisor/animate-proxys/animate-proxy-base";
import AnimateButtonProxy from "SATPROF_LIBRARY/DropinSupervisor/animate-proxys/animate-button-proxy";
import TimingInfo from "SATPROF_LIBRARY/DropinSupervisor/timing-info";
import AnimateRenderSpace from "SATPROF_LIBRARY/DropinSupervisor/render-spaces/animate-render-space";
import UtilSP from "SATPROF_UTILS/UtilSP"; 
import { StringKeyframeTrack, AudioAnalyser } from "three";

export default class RadioButtonGroup extends AnimateProxyBase{
 
	private _selected:number = 0;

	// Settable properties:
	private _nButtons:number = 2; // Number of buttons in use
	private _lastStageButton:number;
	private _texts:Array<createjs.Text>;
	private _labelTexts:Array<string>;
	private _labelFont:string = "16px Arial";
	private _labelSide:string = "right;"
	private _labelColor:string = "#000000";
	private _labelWidth:number = 0;
	public name:string;
    protected _onClickCallback : (e: MouseEvent) =>void = null;
	private _enableClicks:boolean = true;

    onFrameZero(){
        //console.log("In RadioButtonGroup.onFrameZero()");
        super.onFrameZero();
        const animate_rs:AnimateRenderSpace = this.extraData.animateSpace;
        //this.stageSymbol.outline_mc.rotation = 20;
        this.initialize();
    }
	public initialize(){
		let i:number = 0;
		this._texts = [];
		this._labelTexts = []
		//this._labelFont = "16px Arial";


		for (i=0; i<20; i++){
			let rb = this.stageSymbol.getChildByName("rb"+i);
			if (rb){
				rb.target.name = "rb"+i;
				rb.target.addEventListener("click", this.clickHandler.bind(this));
				this._texts[i] = new createjs.Text(); //("label"+i), "20px Arial", "#ff7700");
				rb.addChild(this._texts[i]);
				this._labelTexts[i] = "";
				//console.log("In RadioButtonGroup, found "+rb.target.name);
			} else {
				this._lastStageButton = i-1;
				//console.log("In RadioButtonGroup, _lastStageButton = rb"+this._lastStageButton)
				break;
			}
		}
		this.selected = 0; // Use setter
		this.nButtons = this._lastStageButton+1; // Use setter
		this.updateLabels();
    }  
    onRequestAnimFrame(timingInfo : TimingInfo) {
        // Do every frame
	}  
    public onButtonClick(eventCallback : (e:MouseEvent)=>void ){
        // Allows external function to store a callback function for later use.
        // Define _onPressCallback to be whatever the external function wants saved.
        this._onClickCallback = eventCallback;
    }	
	private clickHandler(e:any){ //e:MouseEvent

		if (!this._enableClicks){
			return;
		}
		let bname:string = e.target.parent.name;
		let i:number = Number(bname.substring(2));
		//console.log("In clickHandler "+evt.target.parent.name+", i = "+i);
		this.selected = i; // use setter

		if(this._onClickCallback){
            // A callback function exists; it must have been stored by an external function 
            // Run that callback function:
            this._onClickCallback(e);		
		}else {
			const event = new CustomEvent('CLICKEDRADIOBUTTON', { detail:{name:this.name} });
			window.dispatchEvent(event)
		}		
	}
	private updateLabels(){
		//console.log("In updateLabels, this._labelSide = "+this._labelSide+
		//	", this._lastStageButton = "+this._lastStageButton+", this._nButtons = "+this._nButtons);
		for (let i=0; i<=this._lastStageButton; i++){
			this._texts[i].y = 5;
			this._texts[i].textBaseline = "middle";
			this._texts[i].color = UtilSP.c2rgba(this._labelColor);
			this._texts[i].font = this._labelFont;
			this._texts[i].text = this._labelTexts[i];
			let lw:any;
			if (this._labelWidth > 0){
				lw = this._labelWidth;
			} else {
				lw = null; // No wrap
			}			
			this._texts[i].lineWidth = lw;
			if (this._labelSide == "left"){
				this._texts[i].x = -15;
				this._texts[i].textAlign = "right";
			} else {
				// Assume right side
				this._texts[i].x = 15;
				this._texts[i].textAlign = "left";
			}
			//console.log("In updateLabels, texts "+i+"= "+i+", "+this._texts[i].text);
		}

	}

	//-----------------------------------------------------------------------------
	// Setters and getters
	/** 
	* Index of the selected button.
	@throws event 'SELECTEDRADIOBUTTON'
	*/	
	public set selected(val:number){
		//console.log("In set selected, val = "+val);
		if (val < -1.1 || val >= this._nButtons){
			return;
		}
		let i:number;
		this._selected = val;
		for (i=0; i<this._nButtons; i++){
			let rb = this.stageSymbol.getChildByName("rb"+i);
			rb.dot.visible = (i == this._selected)
			//console.log("i = "+i+", this._selected = "+this._selected+", rb.dot.visible = "+rb.dot.visible);
		}
		//console.log("In set selected = "+val+". Dispatching event CLICKEDRADIOBUTTON");
		const event = new CustomEvent('SELECTEDRADIOBUTTON', { detail:{name:this.name} });
		window.dispatchEvent(event);
	}
	public get selected():number{
		return this._selected;
	}	
	/** 
	* Sets number of buttons in the group. Set this first. May not exceed 10.
	*/
	public set nButtons(val:number){
		if (val <0 || val > this._lastStageButton+1){
			console.log("ERROR in RadioButtonGroup_p: attempted to set nButtons = "+val+" but _lastStageButton = "+this._lastStageButton)
			return;
		}		
		this._nButtons = val;
		for (let i=0; i<=this._lastStageButton; i++){
			let rb = this.stageSymbol.getChildByName("rb"+i);
			rb.visible = (i < this._nButtons);
			//console.log("button "+i+" vis = "+rb.visible);
		}

	}
	public get nButtons():number{
		return this._nButtons;
	}
	public set labelSide(val:string){
		if ( val == "left" || val == "right"){
			this._labelSide = val;
			this.updateLabels();
		} else {
			console.log("ERROR in in RadioButtonGroup, invalid labelSide "+val)
		}
	}
	public get labelSide(){
		return this._labelSide;
	}
	public set labelFont(val:string){
		this._labelFont = val;
		this.updateLabels();
	}
	public get labelFont(){
		return this._labelFont;
	}	
	public set labelColor(val:string){
		this._labelColor = val;
		this.updateLabels();
	}
	public get labelColor(){
		return this._labelColor;
	}	
	public setLabelText(i:number, str:string){
		this._labelTexts[i] = str;
		this.updateLabels();
	}
	public get labelTexts():Array<string>{
		return this._labelTexts;
	}
	
	public set labelWidth(val:number){
		this._labelWidth = val;
		this.updateLabels();
	}	
	public get labelWidth(){
		return this._labelWidth;
	}
	
	public set enableClicks(val:boolean){
		this._enableClicks = val;
		this.updateLabels();
	}	
	public get enableClicks(){
		return this._enableClicks;
	}
	public set visible(val:boolean){
		this.stageSymbol.visible = val;
	}
	public get visible(){
		return this.stageSymbol.visible;
	}	

}

